import { useQueryClient } from '@tanstack/react-query'

import { getUserInfoCacheKey } from '@termly-web/common'


// What a refused or failed bootstrap leaves behind, which is where signing up
// starts for anybody whose credential the server would not take. `excluded` is
// seeded because consumers read it straight off the record: an absent one
// throws, and an empty one is the truth until the refetch lands.
const EMPTY_CACHE_ENTRY = {
  data: {
    excluded: {},
  },
}


/**
 * Merges freshly-known fields into the cached user record, so that the pages
 * reading it see them without waiting for another request.
 *
 * @returns {Function} which takes the fields to merge
 */
export default function useUpdateUserCache() {
  const queryClient = useQueryClient()

  return (newUserData) => {
    // This function was created when we ran into trouble redirecting
    // users to checkout that come from the pricing page. They would click
    // "sign-up", we would refetch() the user record (from useUserInfoQuery)
    // but the user would inevitably hit <LoginRequired /> as still a guest and
    // be redirected back to /sign-up again, rather than checkout where they wanted
    // to go. Now, after the user clicks 'sign-up', we mark the 'user-info' cache
    // key as stale before resetting it with the new `user` payload.
    //
    // This 'sign-up' flow WILL NOT WORK if you remove `invalidateQueries()`.
    //
    queryClient.invalidateQueries({
      queryKey: getUserInfoCacheKey(),
    })

    queryClient.setQueryData(getUserInfoCacheKey(), ({ data, ...rest } = EMPTY_CACHE_ENTRY) => {
      return {
        ...rest,
        data: {
          ...data,
          ...newUserData,
        },
      }
    })
  }
}
